import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Broker, Property } from '../App';
import './BrokerManagement.css';

interface DistinctEntry { value: string; count: number }

interface GroupSelectorProps {
  label: string;
  field: 'tenant' | 'state' | 'city_state' | 'starred';
  editPropertyIds: string[];
  setEditPropertyIds: React.Dispatch<React.SetStateAction<string[]>>;
}

function GroupSelector({ label, field, editPropertyIds, setEditPropertyIds }: GroupSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState<DistinctEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || field === 'starred') return;
    setLoading(true);
    const controller = new AbortController();
    const params = new URLSearchParams({ field, limit: '10000' });
    fetch(`/api/properties/distinct?${params}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (controller.signal.aborted) return;
        const values: string[] = data.values || [];
        const counts: number[] = data.counts || [];
        setEntries(values.map((v, i) => ({ value: v, count: counts[i] || 0 })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, field]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchIdsForFilter = useCallback(async (filterValue: string): Promise<string[]> => {
    const body: Record<string, string> = {};
    if (field === 'tenant') body.tenant = filterValue;
    else if (field === 'state') body.state = filterValue;
    else if (field === 'city_state') {
      const lastComma = filterValue.lastIndexOf(',');
      if (lastComma > -1) {
        body.city = filterValue.substring(0, lastComma).trim();
        body.state = filterValue.substring(lastComma + 1).trim();
      } else {
        body.city = filterValue;
      }
    } else if (field === 'starred') {
      body.starred = 'starred';
    }
    try {
      const res = await fetch('/api/properties/ids-by-filter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.ids || [];
    } catch { return []; }
  }, [field]);

  const handleCheck = useCallback(async (value: string) => {
    const ids = await fetchIdsForFilter(value);
    setEditPropertyIds((prev) => Array.from(new Set([...prev, ...ids])));
  }, [fetchIdsForFilter, setEditPropertyIds]);

  const handleUncheck = useCallback(async (value: string) => {
    const ids = await fetchIdsForFilter(value);
    const removeSet = new Set(ids);
    setEditPropertyIds((prev) => prev.filter((id) => !removeSet.has(id)));
  }, [fetchIdsForFilter, setEditPropertyIds]);

  if (field === 'starred') {
    return (
      <div className="group-selector" ref={containerRef}>
        <button className="group-selector-btn" onClick={() => handleCheck('starred')}>+ Starred</button>
      </div>
    );
  }

  const q = search.toLowerCase();
  const filtered = q ? entries.filter((e) => e.value.toLowerCase().includes(q)) : entries;

  return (
    <div className="group-selector" ref={containerRef}>
      <button className={`group-selector-btn ${open ? 'active' : ''}`} onClick={() => setOpen((p) => !p)}>
        {label} {open ? '▴' : '▾'}
      </button>
      {open && (
        <div className="group-selector-dropdown">
          <input
            type="text"
            className="group-selector-search"
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="group-selector-list">
            {loading && <div className="group-selector-loading">Loading...</div>}
            {!loading && filtered.length === 0 && <div className="group-selector-loading">No results</div>}
            {!loading && filtered.map((entry) => (
              <div key={entry.value} className="group-selector-item">
                <span className="group-selector-item-label">{entry.value}</span>
                <span className="group-selector-item-count">{entry.count}</span>
                <button className="group-selector-add-btn" onClick={() => handleCheck(entry.value)} title="Add all">+</button>
                <button className="group-selector-remove-btn" onClick={() => handleUncheck(entry.value)} title="Remove all">&minus;</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SELECTED_PAGE_SIZE = 100;

interface SelectedPropertiesListProps {
  editPropertyIds: string[];
  setEditPropertyIds: React.Dispatch<React.SetStateAction<string[]>>;
}

function SelectedPropertiesList({ editPropertyIds, setEditPropertyIds }: SelectedPropertiesListProps) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [editPropertyIds.length, search]);

  useEffect(() => {
    if (!expanded || editPropertyIds.length === 0) {
      setProperties([]);
      setTotal(0);
      return;
    }
    const controller = new AbortController();
    fetch('/api/properties/by-ids', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ids: editPropertyIds,
        q: search.trim() || undefined,
        limit: SELECTED_PAGE_SIZE,
        page,
      }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (controller.signal.aborted) return;
        setProperties(data.properties || []);
        setTotal(data.total ?? editPropertyIds.length);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [expanded, editPropertyIds, page, search]);

  const removeProperty = (id: string) => {
    setEditPropertyIds((prev) => prev.filter((pid) => pid !== id));
  };

  const totalPages = Math.ceil(total / SELECTED_PAGE_SIZE);

  return (
    <div className="selected-properties">
      <div className="selected-properties-header" onClick={() => setExpanded((p) => !p)}>
        <span className="selected-properties-title">
          Selected Properties ({editPropertyIds.length})
          <span className="selected-properties-toggle">{expanded ? '▴' : '▾'}</span>
        </span>
        {editPropertyIds.length > 0 && (
          <button className="selected-properties-clear" onClick={(e) => { e.stopPropagation(); setEditPropertyIds([]); }}>
            Clear All
          </button>
        )}
      </div>
      {expanded && editPropertyIds.length > 0 && (
        <>
          <input
            type="text"
            className="selected-properties-search"
            placeholder="Search selected properties..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="selected-properties-list">
            {properties.map((p) => (
              <div key={p.id} className="selected-property-item">
                <button className="selected-property-remove" onClick={() => removeProperty(p.id)} title="Remove">×</button>
                <span className="selected-property-tenant">{p.tenant || 'N/A'}</span>
                <span className="selected-property-address">{p.address || 'N/A'}</span>
                <span className="selected-property-location">{p.city || ''}{p.city && p.state ? ', ' : ''}{p.state || ''}</span>
              </div>
            ))}
            {properties.length === 0 && <div className="group-selector-loading">Loading...</div>}
          </div>
          {totalPages > 1 && (
            <div className="selected-properties-pagination">
              <span>Page {page} of {totalPages}</span>
              {page > 1 && <button onClick={() => setPage((p) => p - 1)}>Prev</button>}
              {page < totalPages && <button onClick={() => setPage((p) => p + 1)}>Next</button>}
            </div>
          )}
        </>
      )}
      {expanded && editPropertyIds.length === 0 && (
        <div className="selected-properties-empty">No properties selected. Use the group selectors above to add properties.</div>
      )}
    </div>
  );
}

interface BrokerManagementProps {
  brokers: Broker[];
  properties: Property[];
  onCreateBroker: (payload: { name: string; email: string; company: string; username?: string; password?: string }) => Promise<void>;
  onUpdateBroker: (id: string, payload: Partial<Broker>) => Promise<void>;
  onUpdateBrokerPropertyAccess: (id: string, propertyIds: string[]) => Promise<void>;
  onSetBrokerCredentials: (id: string, username: string, password: string) => Promise<void>;
  onDeleteBroker: (id: string) => Promise<void>;
}

const BrokerManagement: React.FC<BrokerManagementProps> = ({
  brokers,
  properties,
  onCreateBroker,
  onUpdateBroker,
  onUpdateBrokerPropertyAccess,
  onSetBrokerCredentials,
  onDeleteBroker,
}) => {
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [editUsername, setEditUsername] = useState('');
  const [editNewPassword, setEditNewPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');

  const selectedBroker = brokers.find((b) => b.id === selectedBrokerId) || null;

  const [editPropertyIds, setEditPropertyIds] = useState<string[]>([]);

  const openCreateModal = () => {
    setShowCreateModal(true);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewCompany('');
    setNewPassword('');
    setCreateError('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateError('');
  };

  const selectBroker = (broker: Broker) => {
    setSelectedBrokerId(broker.id);
    setEditPropertyIds([...broker.propertyIds]);
    setEditUsername(broker.username || '');
    setEditNewPassword('');
    setEditConfirmPassword('');
  };

  const handleCreate = async () => {
    if (!newFirstName.trim() || !newLastName.trim()) {
      setCreateError('First name and last name are required');
      return;
    }
    if (!newEmail.trim()) {
      setCreateError('Email is required');
      return;
    }
    if (!newPassword) {
      setCreateError('Password is required');
      return;
    }
    setIsCreating(true);
    setCreateError('');
    try {
      const fullName = `${newFirstName.trim()} ${newLastName.trim()}`;
      await onCreateBroker({
        name: fullName,
        email: newEmail.trim(),
        company: newCompany.trim(),
        username: newEmail.trim(),
        password: newPassword,
      });
      closeCreateModal();
      setTimeout(() => {
        const newest = brokers.length > 0 ? brokers[brokers.length - 1] : null;
        if (newest) selectBroker(newest);
      }, 300);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create broker');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedBroker) return;
    await onUpdateBroker(selectedBroker.id, {
      name: selectedBroker.name,
      email: selectedBroker.email,
      company: selectedBroker.company,
      isActive: selectedBroker.isActive,
    });
  };

  const handleSaveCredentials = async () => {
    if (!selectedBroker) return;
    if (!editUsername.trim()) { alert('Username is required'); return; }
    if (editNewPassword && editNewPassword !== editConfirmPassword) { alert('Passwords do not match'); return; }
    await onSetBrokerCredentials(selectedBroker.id, editUsername.trim(), editNewPassword);
    setEditNewPassword('');
    setEditConfirmPassword('');
  };

  const handleSavePermissions = async () => {
    if (!selectedBroker) return;
    await onUpdateBrokerPropertyAccess(selectedBroker.id, editPropertyIds);
  };

  const canCreate = newFirstName.trim() && newLastName.trim() && newEmail.trim() && newPassword;

  return (
    <div className="broker-management">
      {/* Create broker modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Broker</h2>
              <button className="modal-close-btn" onClick={closeCreateModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-row">
                <div className="modal-field">
                  <label>First Name *</label>
                  <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} placeholder="John" autoFocus />
                </div>
                <div className="modal-field">
                  <label>Last Name *</label>
                  <input type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} placeholder="Smith" />
                </div>
              </div>
              <div className="modal-field">
                <label>Brokerage Group</label>
                <input type="text" value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="Brokerage name" />
              </div>
              <div className="modal-field">
                <label>Email *</label>
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="broker@example.com" />
              </div>
              <div className="modal-field">
                <label>Password *</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Set a login password" />
              </div>
              {createError && <p className="modal-error">{createError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={closeCreateModal}>Cancel</button>
              <button className="modal-submit-btn" onClick={handleCreate} disabled={!canCreate || isCreating}>
                {isCreating ? 'Creating...' : 'Create Broker'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="broker-list-panel">
        <div className="broker-list-header">
          <h2>Brokers</h2>
          <button className="broker-add-btn" onClick={openCreateModal}>+ Add New Broker</button>
        </div>

        <div className="broker-list">
          {brokers.map((broker) => (
            <button key={broker.id} className={`broker-list-item ${selectedBrokerId === broker.id ? 'active' : ''}`} onClick={() => selectBroker(broker)}>
              <div className="broker-list-item-info">
                <span className="broker-list-name">{broker.name}</span>
                {broker.email && <span className="broker-list-email">{broker.email}</span>}
                {broker.company && <span className="broker-list-email">{broker.company}</span>}
              </div>
              <span className="broker-list-meta">{broker.propertyIds.length} properties</span>
            </button>
          ))}
          {brokers.length === 0 && (
            <div className="broker-empty">
              <p>No brokers yet.</p>
              <p>Click "+ Add New Broker" to create one.</p>
            </div>
          )}
        </div>
      </div>

      {selectedBroker ? (
        <div className="broker-detail-panel">
          <div className="broker-profile-card">
            <h3>{selectedBroker.name}</h3>
            <div className="broker-profile-fields">
              <label>Email<input type="text" value={selectedBroker.email} readOnly /></label>
              <label>Brokerage Group<input type="text" value={selectedBroker.company} readOnly /></label>
              <label className="broker-active-label">
                <input type="checkbox" checked={selectedBroker.isActive} onChange={(e) => onUpdateBroker(selectedBroker.id, { isActive: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="broker-profile-actions">
              <button className="save-btn" onClick={handleSaveProfile}>Save Profile</button>
              <button className="delete-btn" onClick={() => onDeleteBroker(selectedBroker.id)}>Delete Broker</button>
            </div>
          </div>

          <div className="broker-credential-card">
            <h3>Broker Login Credentials</h3>
            <div className="broker-profile-fields">
              <label>Username<input type="text" value={editUsername} onChange={(e) => setEditUsername(e.target.value)} /></label>
              <label>New Password<input type="password" value={editNewPassword} onChange={(e) => setEditNewPassword(e.target.value)} placeholder="Leave blank to keep current" /></label>
              <label>Confirm Password<input type="password" value={editConfirmPassword} onChange={(e) => setEditConfirmPassword(e.target.value)} /></label>
            </div>
            <span className="broker-credential-status">
              {selectedBroker.hasCredentials ? 'Credentials set' : 'No credentials set'}
            </span>
            <button className="save-btn" onClick={handleSaveCredentials}>Save Credentials</button>
          </div>

          <div className="broker-permissions-card">
            <h3>Property Permissions ({editPropertyIds.length})</h3>

            <div className="group-selectors-section">
              <span className="group-selectors-label">Add by Group</span>
              <div className="group-selectors-row">
                <GroupSelector label="Tenant" field="tenant" editPropertyIds={editPropertyIds} setEditPropertyIds={setEditPropertyIds} />
                <GroupSelector label="State" field="state" editPropertyIds={editPropertyIds} setEditPropertyIds={setEditPropertyIds} />
                <GroupSelector label="City" field="city_state" editPropertyIds={editPropertyIds} setEditPropertyIds={setEditPropertyIds} />
                <GroupSelector label="Starred" field="starred" editPropertyIds={editPropertyIds} setEditPropertyIds={setEditPropertyIds} />
              </div>
            </div>

            <SelectedPropertiesList editPropertyIds={editPropertyIds} setEditPropertyIds={setEditPropertyIds} />

            <button className="save-btn" onClick={handleSavePermissions} style={{ marginTop: '0.75rem' }}>Save Permissions</button>
          </div>
        </div>
      ) : (
        <div className="broker-detail-panel broker-detail-empty">
          <p>Select a broker to manage their profile, credentials, and property permissions.</p>
        </div>
      )}
    </div>
  );
};

export default BrokerManagement;
